import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { getFirestore, collection, query as queryFire, where, getDocs } from "firebase/firestore";
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Grid } from '@mui/material';
import logo from '../../cbalogo.jpeg';
import { formatDate } from '../../utils/dateUtils';

const firestore = getFirestore();

function FacturaRemitoHerramientas() {
    let { id } = useParams();
    const navigate = useNavigate();

    const [factura, setFactura] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getById(parseInt(id, 10));
    }, [id]);

    const getById = async (remitoId) => {
        setLoading(true);
        try {
            const remitosCollection = collection(firestore, 'remitos_herramientas');
            const q = queryFire(remitosCollection, where('id', '==', remitoId));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                querySnapshot.forEach((doc) => {
                    setFactura(doc.data());
                });
            } else {
                console.log("No se encontró ningún remito con ese ID.");
            }
        } catch (error) {
            console.error("Error al obtener el remito:", error);
        } finally {
            setLoading(false);
        }
    };

    const print = () => {
        window.print();
    };

    const goBackToList = () => {
        navigate('/herramientas-remitos-list');
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!factura) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="error">Remito no encontrado</Typography>
                <Button onClick={goBackToList} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
                    Volver al listado
                </Button>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="md" className="print-container-main" sx={{ py: 3 }}>
            <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Button onClick={goBackToList} startIcon={<ArrowBackIcon />}>
                    Volver
                </Button>
                <Button onClick={print} variant="contained" startIcon={<PrintIcon />}>
                    Imprimir
                </Button>
            </Box>

            <Paper className="print-layout-paper" sx={{ p: 4, border: '1px solid #ddd' }}>
                {/* Header of Invoice */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #1976d2', pb: 2, mb: 3 }}>
                    <Box>
                        <img src={logo} alt="Logo" style={{ width: '120px', height: 'auto' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            SUCRE 3085, CÓRDOBA, ARG.
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                            REMITO HERRAMIENTAS
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Nº {factura.id}
                        </Typography>
                        <Typography variant="body2">
                            Fecha Confección: {factura.confectionDate || ''}
                        </Typography>
                    </Box>
                </Box>

                {/* Details Section */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                ORIGEN / ENTREGA
                            </Typography>
                            <Typography variant="body1">
                                {factura.tipoRemito === 'ENTREGA' ? 'Depósito cba' : factura.cuadrillaEntrega || 'Cuadrilla Emisora'}
                            </Typography>
                            {factura.usuarioCuadrillaEntrega && (
                                <Typography variant="body2" color="text.secondary">
                                    Usuario: {factura.usuarioCuadrillaEntrega}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                DESTINO / RECEPCIÓN
                            </Typography>
                            <Typography variant="body1">
                                {factura.tipoRemito === 'DEVOLUCION' ? 'Depósito cba' : factura.cuadrillaReceptora || 'Cuadrilla Receptora'}
                            </Typography>
                            {factura.usuarioCuadrillaReceptora && (
                                <Typography variant="body2" color="text.secondary">
                                    Usuario: {factura.usuarioCuadrillaReceptora}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2">
                            <strong>Tipo de Movimiento:</strong> {factura.tipoRemito}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2">
                            <strong>Fecha Remito:</strong> {factura.fecha ? formatDate(factura.fecha) : ''}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Materials/Tools Table */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>Código / ID</strong></TableCell>
                                <TableCell><strong>Herramienta</strong></TableCell>
                                <TableCell align="right"><strong>Cantidad</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {factura.materials && factura.materials.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell align="right">{item.value}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Observations */}
                {factura.observaciones && (
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Observaciones:
                        </Typography>
                        <Typography variant="body2" sx={{ p: 2, bg: '#fafafa', border: '1px solid #eee', borderRadius: '4px' }}>
                            {factura.observaciones}
                        </Typography>
                    </Box>
                )}

                {/* Signatures */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 8, pt: 4, borderTop: '1px dashed #ccc' }}>
                    <Box sx={{ width: '40%', textAlign: 'center' }}>
                        <Box sx={{ borderTop: '1px solid #000', mx: 2, pt: 1 }}>
                            <Typography variant="body2">Firma Entrega (Origen)</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ width: '40%', textAlign: 'center' }}>
                        <Box sx={{ borderTop: '1px solid #000', mx: 2, pt: 1 }}>
                            <Typography variant="body2">Firma Recepción (Destino)</Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Print styling injection */}
            <style>
                {`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    body {
                        background-color: #fff !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .print-container-main {
                        max-width: 100% !important;
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .print-layout-paper {
                        border: none !important;
                        box-shadow: none !important;
                        padding: 0 !important;
                    }
                }
                `}
            </style>
        </Container>
    );
}

export default FacturaRemitoHerramientas;
